import {
  RECIPE_DIMENSIONS,
  REVIEW_SURFACES,
  buildAgentSignalFrequencyRows,
  buildRecipeMatrixRows,
  getReviewSurfacesByCadence,
  recipeMatrixAcceptanceRule,
  summarizeReviewStatus,
  type AgentSignalFrequencyRow,
  type RecipeMatrixRow,
  type RecipeSignal,
} from "../lib/ctoReview";
import type { QueryState } from "../hooks/useQueryWithState";

interface CtoReviewWorkspaceProps {
  signalsState?: QueryState<RecipeSignal[]>;
}

export function CtoReviewWorkspace({ signalsState }: CtoReviewWorkspaceProps) {
  const status = summarizeReviewStatus();
  const weekly = getReviewSurfacesByCadence("weekly");
  const monthly = getReviewSurfacesByCadence("monthly");
  const liveSignals = signalsState?.status === "success" ? signalsState.data : [];
  const recipeRows = buildRecipeMatrixRows(liveSignals);
  const frequencyRows = buildAgentSignalFrequencyRows(liveSignals);

  return (
    <main className="cto-review" aria-label="CTO data science review">
      <header className="cto-review-hero">
        <span className="cto-review-kicker">CTO · AI quant data science</span>
        <h2>Recipe Matrix + Review Memory</h2>
        <p>
          Separate read-only workspace for recipe-combination research, weekly review,
          monthly review, and the inventory of shipped research surfaces. Nothing here
          feeds entries, exits, gates, sizing, alerts, or FBE weights.
        </p>
      </header>

      <section className="cto-review-grid" aria-label="Review status summary">
        <Metric label="Active surfaces" value={status.active} />
        <Metric label="Collecting" value={status.collecting} />
        <Metric label="Watch-only" value={status.watch_only} />
        <Metric label="Blocked" value={status.blocked} />
      </section>

      <section className="cto-review-card" aria-label="Recipe Attribution Matrix">
        <div className="cto-review-section-head">
          <div>
            <span className="cto-review-kicker">LOG-ONLY</span>
            <h3>Recipe Attribution Matrix</h3>
          </div>
          <span className="cto-review-pill">Shadow only</span>
        </div>
        <p>
          Search for stable combinations like <strong>FBD reclaim + Kronos up + HMM trending_down + SMA aligned + bullish flow</strong>,
          then score them by win rate, PnL, and symbol/session breadth. Symbol/session are shown as robustness proof,
          not baked into the recipe key.
        </p>
        <div className="cto-recipe-dimensions">
          {RECIPE_DIMENSIONS.map((dimension) => (
            <div key={dimension.id} className="cto-recipe-dimension">
              <strong>{dimension.label}</strong>
              <span>{dimension.examples.join(" · ")}</span>
            </div>
          ))}
        </div>
        <p className="cto-review-rule">{recipeMatrixAcceptanceRule()}</p>
        <RecipeMatrixTable rows={recipeRows} state={signalsState} />
      </section>

      <section className="cto-review-card" aria-label="FBD MR signal scarcity monitor">
        <div className="cto-review-section-head">
          <div>
            <span className="cto-review-kicker">Frequency guardrail</span>
            <h3>FBD/MR Signal Scarcity Monitor</h3>
          </div>
          <span className="cto-review-pill">No gate changes</span>
        </div>
        <p>
          Track whether FBD and MR are under-sending relative to ORB using the same recent Convex signal sample.
          This is an evidence step before subscriber-facing near-miss alerts or any gate relaxation.
        </p>
        <AgentFrequencyTable rows={frequencyRows} state={signalsState} />
      </section>

      <section className="cto-review-columns">
        <ReviewCadenceCard title="Weekly review" rows={weekly} />
        <ReviewCadenceCard title="Monthly review" rows={monthly} />
      </section>

      <section className="cto-review-card" aria-label="Built surface inventory">
        <div className="cto-review-section-head">
          <div>
            <span className="cto-review-kicker">Inventory</span>
            <h3>Do not forget what already exists</h3>
          </div>
        </div>
        <div className="cto-surface-list">
          {REVIEW_SURFACES.map((surface) => (
            <article key={surface.id} className="cto-surface-row">
              <div>
                <strong>{surface.name}</strong>
                <span>{surface.ownerTab}</span>
              </div>
              <p>{surface.purpose}</p>
              <small>{surface.nextReviewQuestion}</small>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="cto-review-metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function formatPct(value: number | null): string {
  return value === null ? "—" : `${Math.round(value * 1000) / 10}%`;
}

function formatDollars(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  return `${sign}$${Math.abs(Math.round(value)).toLocaleString()}`;
}

function recipeStatusLabel(status: RecipeMatrixRow["status"]): string {
  if (status === "manual_review") return "Manual review";
  if (status === "discussion") return "Discussion";
  return "Collect";
}

function frequencyStatusLabel(status: AgentSignalFrequencyRow["status"]): string {
  if (status === "starved") return "Starved";
  if (status === "thin_sample") return "Thin sample";
  return "Collecting";
}

function RecipeMatrixTable({ rows, state }: { rows: RecipeMatrixRow[]; state?: QueryState<RecipeSignal[]> }) {
  if (!state || state.status === "loading") {
    return <div className="cto-recipe-empty">Loading live recipe rows from recent signals…</div>;
  }
  if (state.status === "error") {
    return (
      <div className="cto-recipe-empty">
        Recipe data unavailable. <button type="button" onClick={state.retry}>Retry</button>
      </div>
    );
  }
  if (rows.length === 0) {
    return <div className="cto-recipe-empty">No closed signal recipes in the current sample yet.</div>;
  }
  return (
    <div className="cto-recipe-table" aria-label="Live recipe matrix">
      <div className="cto-recipe-table-head">
        <span>Recipe</span><span>N</span><span>WR</span><span>P&L</span><span>Breadth</span><span>Status</span>
      </div>
      {rows.map((row) => (
        <div key={row.recipe} className="cto-recipe-table-row">
          <span>{row.recipe}</span>
          <span>{row.n}</span>
          <span>{formatPct(row.winRate)}</span>
          <span>{formatDollars(row.totalPnl)}</span>
          <span>{row.robustness}: {row.symbols.join("/")} · {row.sessions.join("/")}</span>
          <span>{recipeStatusLabel(row.status)}</span>
        </div>
      ))}
    </div>
  );
}

function AgentFrequencyTable({ rows, state }: { rows: AgentSignalFrequencyRow[]; state?: QueryState<RecipeSignal[]> }) {
  if (!state || state.status === "loading") {
    return <div className="cto-recipe-empty">Loading agent frequency from recent signals…</div>;
  }
  if (state.status === "error") {
    return (
      <div className="cto-recipe-empty">
        Agent frequency unavailable. <button type="button" onClick={state.retry}>Retry</button>
      </div>
    );
  }
  return (
    <div className="cto-frequency-table" aria-label="Agent signal frequency table">
      <div className="cto-frequency-table-head">
        <span>Agent</span><span>Sends</span><span>Share</span><span>Sessions</span><span>Status</span><span>Next data-science step</span>
      </div>
      {rows.map((row) => (
        <div key={row.agent} className="cto-frequency-table-row">
          <span>{row.agent.toUpperCase()}</span>
          <span>{row.n}</span>
          <span>{formatPct(row.share)}</span>
          <span>{row.sessions.length > 0 ? row.sessions.join("/") : "—"}</span>
          <span>{frequencyStatusLabel(row.status)}</span>
          <span>{row.recommendation}</span>
        </div>
      ))}
    </div>
  );
}

function ReviewCadenceCard({ title, rows }: { title: string; rows: ReturnType<typeof getReviewSurfacesByCadence> }) {
  return (
    <section className="cto-review-card" aria-label={title}>
      <div className="cto-review-section-head">
        <h3>{title}</h3>
        <span className="cto-review-pill">{rows.length} surfaces</span>
      </div>
      <ul className="cto-review-checklist">
        {rows.map((row) => (
          <li key={row.id}>
            <strong>{row.name}</strong>
            <span>{row.nextReviewQuestion}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
